import CopyableId from "@/components/common/CopyableId";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Siren, Ambulance, Timer, CheckCircle2, AlertTriangle, RefreshCw,
  ExternalLink, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABEL = { rpm_alert: "RPM Alert", hotline_session: "Hotline", manual: "Manual" };
const TYPE_COLOR = { rpm_alert: "#B91C1C", hotline_session: "#1D4ED8", manual: "#7C3AED" };

function fmtMins(m) {
  if (m == null) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${Math.round(m % 60)}m`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Kpi({ icon: Icon, accent, label, value, caption, testId }) {
  return (
    <div className="hah-card p-3 flex items-start gap-3" data-testid={testId}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-2xl font-bold text-[#1F2937]">{value ?? "—"}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
        {caption && <div className="text-[10px] text-[#94A3B8] mt-0.5">{caption}</div>}
      </div>
    </div>
  );
}

export default function SosLogDashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/critical/sos/metrics", { params: { days: Number(days) } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  async function resolve(ev) {
    setBusyId(ev.id);
    try {
      await api.post(`/critical/sos/${ev.id}/resolve`);
      toast.success("SOS marked resolved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  const t = data?.totals || {};
  const events = data?.events || [];

  return (
    <div data-testid="sos-log-dashboard">
      <PageHeader
        title="SOS & Critical-Case Log"
        subtitle="Audit trail of every SOS escalation — volume, response times and resolution status."
        actions={
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32" data-testid="sos-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="border-[#E5E7EB]" onClick={load} data-testid="sos-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <Kpi icon={Siren} accent="#B91C1C" label="Total SOS" value={t.total} testId="sos-kpi-total" />
        <Kpi icon={AlertTriangle} accent="#F59E0B" label="Active" value={t.active} testId="sos-kpi-active" />
        <Kpi icon={CheckCircle2} accent="#15803D" label="Resolved" value={t.resolved} testId="sos-kpi-resolved" />
        <Kpi icon={Ambulance} accent="#2563EB" label="Ambulance" value={t.ambulance_requested} testId="sos-kpi-ambulance" />
        <Kpi icon={Timer} accent="#0B7E8C" label="Avg Response" value={fmtMins(data?.avg_response_minutes)} testId="sos-kpi-avg" />
        <Kpi icon={Clock} accent="#7C3AED" label="Median Response" value={fmtMins(data?.median_response_minutes)} testId="sos-kpi-median" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="hah-card p-4 lg:col-span-2" data-testid="sos-trend-card">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#6B7280] mb-2">SOS Volume Trend</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.trend || []}>
              <defs>
                <linearGradient id="sosFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B91C1C" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#B91C1C" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#B91C1C" fill="url(#sosFill)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="hah-card p-4" data-testid="sos-bytype-card">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#6B7280] mb-2">By Source</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={(data?.by_case_type || []).map((d) => ({ ...d, label: TYPE_LABEL[d.type] || d.type }))} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={70} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {(data?.by_case_type || []).map((d, i) => <Cell key={i} fill={TYPE_COLOR[d.type] || "#94A3B8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="hah-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[#F1F5F9] text-[11px] uppercase tracking-wider font-bold text-[#6B7280]">
          Recent SOS Events
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left p-2.5">Patient</th>
              <th className="text-left p-2.5">Source</th>
              <th className="text-left p-2.5">Triggered By</th>
              <th className="text-left p-2.5">Triggered At</th>
              <th className="text-left p-2.5">Response</th>
              <th className="text-left p-2.5">Status</th>
              <th className="text-right p-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>}
            {!loading && events.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-sm text-[#6B7280]">No SOS events in this window.</td></tr>}
            {events.map((e) => (
              <tr key={e.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`sos-row-${e.id}`}>
                <td className="p-2.5">
                  <div className="font-semibold text-[#1F2937]">{e.patient_name || "—"}</div>
                  {e.mrn && <div className="text-xs text-[#6B7280] font-mono"><CopyableId value={e.mrn} kind="MRN" /></div>}
                  {e.reason && <div className="text-xs text-[#94A3B8] max-w-[260px] truncate" title={e.reason}>{e.reason}</div>}
                </td>
                <td className="p-2.5">
                  <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded"
                    style={{ background: `${TYPE_COLOR[e.case_type] || "#94A3B8"}1A`, color: TYPE_COLOR[e.case_type] || "#475569" }}>
                    {TYPE_LABEL[e.case_type] || e.case_type}
                  </span>
                  {e.ambulance_requested && <span className="ml-1.5 text-[10px] text-[#2563EB] font-semibold inline-flex items-center gap-0.5"><Ambulance className="w-3 h-3" />Ambulance</span>}
                </td>
                <td className="p-2.5 text-[#6B7280]">{e.triggered_by_name || "—"}</td>
                <td className="p-2.5 text-[#6B7280]">{fmtDate(e.created_at)}</td>
                <td className="p-2.5 text-[#1F2937] font-medium">{fmtMins(e.response_minutes)}</td>
                <td className="p-2.5">
                  {e.status === "resolved" ? (
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: "#DCFCE7", color: "#15803D" }}>Resolved</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider animate-pulse" style={{ background: "#FEE2E2", color: "#B91C1C" }}>Active</span>
                  )}
                </td>
                <td className="p-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    {e.patient_id && (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-[#E5E7EB]"
                        onClick={() => navigate(`/clinical/patients/${e.patient_id}`)} data-testid={`sos-open-${e.id}`}>
                        <ExternalLink className="w-3 h-3 mr-1" />Patient
                      </Button>
                    )}
                    {e.status === "active" && (
                      <Button size="sm" disabled={busyId === e.id} className="h-7 text-xs bg-[#15803D] hover:bg-[#166534] text-white"
                        onClick={() => resolve(e)} data-testid={`sos-resolve-${e.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />Resolve
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
